// Browser push subscription helpers. docs/replan-v2.md R11.
//
// Flow: fetch the server's VAPID public key → ask the SW's PushManager to
// subscribe with it → POST the resulting subscription to the API. The queue
// consumer then delivers Web Push to this device. All guarded so unsupported
// browsers (or a missing VAPID key) simply no-op.

import { api } from './api';

export function pushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

// Async to match the rest of this module's Promise-based API surface; every
// caller awaits it alongside genuinely async calls.
// eslint-disable-next-line @typescript-eslint/require-await
export async function pushPermission(): Promise<NotificationPermission> {
  if (!pushSupported()) return 'denied';
  return Notification.permission;
}

export async function isSubscribed(): Promise<boolean> {
  if (!pushSupported()) return false;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  return sub !== null;
}

async function vapidKey(): Promise<string> {
  const { key } = await api<{ key: string }>('/notifications/push/vapid-key');
  return key;
}

function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

// Request permission + subscribe this device. Returns true on success. Throws
// only on an unexpected error; permission-denied resolves to false.
export async function subscribeToPush(): Promise<boolean> {
  if (!pushSupported()) return false;
  const key = await vapidKey();
  if (!key) return false; // push not configured server-side

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return false;

  const reg = await navigator.serviceWorker.ready;
  const existing = await reg.pushManager.getSubscription();
  const sub =
    existing ??
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(key),
    }));

  const json = sub.toJSON();
  await api('/notifications/push/subscribe', {
    method: 'POST',
    body: {
      endpoint: json.endpoint,
      keys: { p256dh: json.keys?.p256dh, auth: json.keys?.auth },
    },
  });
  return true;
}

export async function unsubscribeFromPush(): Promise<void> {
  if (!pushSupported()) return;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  const endpoint = sub.endpoint;
  await sub.unsubscribe();
  try {
    await api('/notifications/push/unsubscribe', { method: 'POST', body: { endpoint } });
  } catch {
    // server-side cleanup is best-effort; the row prunes itself on 410 later.
  }
}
